import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Badge, Button, Card, Input, Radio, Select, Space, Tabs, Tag } from "antd";
import type { PlatClueForm, PlatClueVo, PlatClueCountVo } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";
import { DATE_PICKER_FORMAT, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { dateFormat } from "@/utils";
import { getValueByPath } from "@/utils/object";
import { useUserStore } from "@/stores/react/authStore";
import { QUOTE_IDENTITY } from "@/meta/logistics/shipNeed";
import { TRANSPORT_CAPACITY_E_TYPE, TRANSPORT_CAPACITY_TYPE_STATE } from "@zhihaoscm/meta";
import { demandColumns, emptyShipColumns, quoteColumns, userColumns } from "./columns";

type Row = PlatClueVo & { platClue: PlatClueVo["platClue"] };
export type PlatformClueQuery = Omit<PlatClueForm, "identity"> & { keyWord?: string; identity?: string | string[] };
export type PlatformClueUpdate = { state: number; remark: string; handlerId?: number };

export async function loadPlatformClues(query: PlatformClueQuery) {
    const { platClueService } = await import("@/api/services/platClueService");
    return platClueService.getPlatCluePaging(query as unknown as PlatClueForm);
}
export async function loadPlatformClueCount() {
    const { platClueService } = await import("@/api/services/platClueService");
    return platClueService.getPlatClueCount();
}
export async function dealPlatformClue(id: number | string, form: PlatformClueUpdate) {
    const { platClueService } = await import("@/api/services/platClueService");
    return platClueService.platClueDeal(id, { ...form, handlerId: form.handlerId || 0 });
}

const tabs = [
    { key: "1", label: "注册用户信息表" },
    { key: "2", label: "找船需求信息表" },
    { key: "3", label: "报价信息表" },
    { key: "4", label: "空船信息表" }
];
const dateScopes = [
    { label: "全部", value: 0 },
    { label: "今日", value: 1 },
    { label: "近一周", value: 2 },
    { label: "近一月", value: 3 },
    { label: "近一年", value: 5 }
];
const statusLabel = (state?: number) => ({ 0: "未处理", 1: "持续跟进", 2: "已处理" })[Number(state)] || "未知";
const countForTab = (tab: string, count: PlatClueCountVo) =>
    ({
        "1": count.customerCount,
        "2": count.requirementPlatCount,
        "3": count.requirementAcceptCount,
        "4": count.emptyShipCount
    })[tab] || 0;
const columnMap = {
    [TRANSPORT_CAPACITY_E_TYPE.NEW_USER]: userColumns,
    [TRANSPORT_CAPACITY_E_TYPE.NEW_SHIPPING_REQUIREMENT_PLAT]: demandColumns,
    [TRANSPORT_CAPACITY_E_TYPE.NEW_SHIPPING_REQUIREMENT_ACCEPT]: quoteColumns,
    [TRANSPORT_CAPACITY_E_TYPE.NEW_EMPTY_SHIP]: emptyShipColumns
};

export interface PlatformCluesManagePageProps {
    loadClues?: (query: PlatformClueQuery) => Promise<{ list?: Row[]; total?: number }>;
    loadCount?: () => Promise<PlatClueCountVo>;
    dealClue?: (id: number | string, form: PlatformClueUpdate) => Promise<unknown>;
    permissions?: string[];
}

/** 平台线索管理：按 Vue 的 type、scope、paging 和处理状态组织四类线索。 */
export function PlatformCluesManagePage({
    loadClues = loadPlatformClues,
    loadCount = loadPlatformClueCount,
    dealClue = dealPlatformClue,
    permissions
}: PlatformCluesManagePageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const canWrite = (permissions || storePermissions).includes("ROLE_PLATFORM_CLUE_W");
    const [tab, setTab] = useState("1");
    // 保留 Vue ProTable 的当前页，翻页时作为 paging 接口的 page 参数提交。
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState<Row[]>([]);
    const [total, setTotal] = useState(0);
    const [count, setCount] = useState<PlatClueCountVo>({} as PlatClueCountVo);
    const [keyword, setKeyword] = useState("");
    const [scope, setScope] = useState<number | null>(0);
    const [dateRange, setDateRange] = useState<[string, string] | null>(null);
    const [state, setState] = useState<number[]>([]);
    const [identity, setIdentity] = useState<string[]>([]);
    const [appliedFilters, setAppliedFilters] = useState({
        keyword: "",
        scope: 0 as number | null,
        dateRange: null as [string, string] | null,
        state: [] as number[],
        identity: [] as string[]
    });
    const [error, setError] = useState("");
    const [editing, setEditing] = useState<Row | null>(null);
    const [remark, setRemark] = useState("");
    const [processState, setProcessState] = useState(2);
    const [saving, setSaving] = useState(false);
    // 处理成功后即使筛选值未变化，也要显式触发 Vue 列表语义对应的重新加载。
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        let active = true;
        void loadCount()
            .then(setCount)
            .catch(() => undefined);
        const query: PlatformClueQuery = {
            page,
            size: 10,
            type: Number(tab),
            scope: appliedFilters.scope ?? 0,
            ...(appliedFilters.keyword ? { keyWord: appliedFilters.keyword } : {}),
            ...(appliedFilters.state.length ? { state: appliedFilters.state } : {}),
            ...(appliedFilters.identity.length ? { identity: appliedFilters.identity } : {}),
            ...(appliedFilters.dateRange?.[0]
                ? { beginTime: dateFormat(appliedFilters.dateRange[0], LOG_DATE_FORMAT_START) }
                : {}),
            ...(appliedFilters.dateRange?.[1]
                ? { endTime: dateFormat(appliedFilters.dateRange[1], LOG_DATE_FORMAT_END) }
                : {})
        };
        void loadClues(query)
            .then(result => {
                if (!active) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (active) {
                    setRows([]);
                    setTotal(0);
                    setError("平台线索加载失败");
                }
            });
        return () => {
            active = false;
        };
    }, [loadClues, loadCount, tab, page, appliedFilters, refreshToken]);

    const process = async () => {
        if (!editing || saving) return;
        setSaving(true);
        try {
            await dealClue(editing.platClue.id, { state: processState, remark });
            setEditing(null);
            setRemark("");
            setRefreshToken(value => value + 1);
        } catch {
            setError("平台线索处理失败");
        } finally {
            setSaving(false);
        }
    };
    // 用 zhihao-ui-react 的列契约承接旧 Vue 四类 ProTable 字段和处理插槽。
    const columns = useMemo<TableColumnProps<Row>[]>(() => {
        const legacyColumns = columnMap[Number(tab) as TRANSPORT_CAPACITY_E_TYPE] || [];
        return (legacyColumns as unknown as TableColumnProps<Row>[]).map((column, columnIndex) => ({
            ...column,
            key: column.key || column.prop || `column-${columnIndex}`,
            render: ({ row, rowIndex }) => {
                if (column.prop === "index") {
                    return (
                        <Link to={`/logistics/cluesManage/platformCluesManage/detail/${row.platClue.id}?tab=${tab}`}>
                            {(page - 1) * 10 + rowIndex + 1}
                        </Link>
                    );
                }
                if (column.prop === "operation") {
                    return canWrite && [0, 1].includes(Number(row.platClue.state)) ? (
                        <Button
                            type="link"
                            onClick={() => {
                                setEditing(row);
                                setRemark("");
                                setProcessState(2);
                            }}
                        >
                            处理
                        </Button>
                    ) : null;
                }
                if (column.prop === "platClue.state") return <Tag>{statusLabel(row.platClue.state)}</Tag>;
                const cellValue = getValueByPath(row as unknown as Record<string, unknown>, String(column.prop || ""));
                if (column.formatter) {
                    // 后端偶尔返回缺少关联业务对象的残缺线索，格式化失败时沿用 Vue 的缺省展示。
                    try {
                        return column.formatter(row, column, cellValue, rowIndex) ?? "--";
                    } catch {
                        return "--";
                    }
                }
                return cellValue ?? "--";
            }
        }));
    }, [canWrite, page, tab]);

    /** 仅在点击查询时提交筛选，保持 Vue search-form 的显式触发语义。 */
    const submitFilters = () => {
        setPage(1);
        setAppliedFilters({
            keyword: keyword.trim(),
            scope,
            dateRange,
            state,
            identity
        });
    };
    /** 切换线索类型时清空旧筛选，避免跨 tab 继承 Vue 未保留的条件。 */
    const changeTab = (nextTab: string) => {
        setTab(nextTab);
        setPage(1);
        setScope(0);
        setDateRange(null);
        setState([]);
        setIdentity([]);
        setAppliedFilters({ keyword: "", scope: 0, dateRange: null, state: [], identity: [] });
    };
    const resetFilters = () => {
        setKeyword("");
        setScope(0);
        setDateRange(null);
        setState([]);
        setIdentity([]);
        setPage(1);
        setAppliedFilters({ keyword: "", scope: 0, dateRange: null, state: [], identity: [] });
    };

    return (
        <section aria-label="platform-clues-manage-page">
            <Card title="平台线索管理">
                <Tabs
                    activeKey={tab}
                    items={tabs.map(item => ({
                        ...item,
                        label: (
                            <span>
                                {item.label}
                                {countForTab(item.key, count) > 0 ? (
                                    <Badge count={countForTab(item.key, count)} overflowCount={99} />
                                ) : null}
                            </span>
                        )
                    }))}
                    onChange={changeTab}
                />
                <Space>
                    <Input
                        aria-label="平台线索关键词"
                        placeholder="请输入关键词"
                        value={keyword}
                        onChange={event => setKeyword(event.target.value)}
                    />
                    {dateScopes.map(item => (
                        <Button
                            key={item.value}
                            type={scope === item.value ? "primary" : "default"}
                            onClick={() => {
                                setScope(item.value);
                                setDateRange(null);
                            }}
                        >
                            {item.label}
                        </Button>
                    ))}
                    <ZhDateRangePicker
                        aria-label="线索日期范围"
                        rangeSeparator="-"
                        type="daterange"
                        format={DATE_PICKER_FORMAT}
                        valueFormat={DATE_PICKER_FORMAT}
                        startPlaceholder="线索开始日期"
                        endPlaceholder="线索结束日期"
                        value={dateRange}
                        onChange={(_value, formattedValue) => {
                            setDateRange(formattedValue as [string, string] | null);
                            setScope(null);
                        }}
                    />
                    <Select
                        aria-label="处理状态"
                        mode="multiple"
                        allowClear
                        placeholder="请选择处理状态"
                        value={state}
                        options={TRANSPORT_CAPACITY_TYPE_STATE.options}
                        onChange={setState}
                    />
                    {Number(tab) === TRANSPORT_CAPACITY_E_TYPE.NEW_SHIPPING_REQUIREMENT_ACCEPT ? (
                        <Select
                            aria-label="报价身份"
                            mode="multiple"
                            allowClear
                            placeholder="请选择身份"
                            value={identity}
                            options={QUOTE_IDENTITY.options}
                            onChange={setIdentity}
                        />
                    ) : null}
                    <Button type="primary" onClick={submitFilters}>
                        查询
                    </Button>
                    <Button onClick={resetFilters}>重置</Button>
                </Space>
                <div className="mt-3">
                    注册用户：{count.customerCount || 0} 找船需求：{count.requirementPlatCount || 0} 报价：
                    {count.requirementAcceptCount || 0} 空船：{count.emptyShipCount || 0}
                </div>
            </Card>
            {error ? <Alert message={error} type="error" showIcon /> : null}
            <Card>
                <ZhTable<Row>
                    rowKey={row => String(row.platClue.id)}
                    data={rows}
                    columns={columns}
                    pagination={false}
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={page}
                                pageSize={10}
                                total={total}
                                hideOnSinglePage
                                onChange={nextPage => setPage(nextPage)}
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                open={Boolean(editing)}
                title="处理平台线索"
                width={400}
                appendToBody
                footer={
                    <Space>
                        <Button disabled={saving} onClick={() => setEditing(null)}>
                            取消
                        </Button>
                        <Button loading={saving} type="primary" onClick={() => void process()}>
                            确认处理
                        </Button>
                    </Space>
                }
                onCancel={() => {
                    if (!saving) setEditing(null);
                }}
            >
                <ZhGrid cols={1} gap={[0, 0]}>
                    <ZhGridItem label="是否处理">
                        <Radio.Group
                            aria-label="是否处理"
                            value={processState}
                            options={TRANSPORT_CAPACITY_TYPE_STATE.options.filter(item => item.value !== 0)}
                            onChange={event => setProcessState(event.target.value)}
                        />
                    </ZhGridItem>
                    <ZhGridItem label="处理说明">
                        <Input.TextArea
                            aria-label="处理备注"
                            rows={4}
                            value={remark}
                            onChange={event => setRemark(event.target.value)}
                        />
                    </ZhGridItem>
                </ZhGrid>
            </ZhDialog>
        </section>
    );
}

export default PlatformCluesManagePage;
